import { useState } from 'react';
import {
  X,
  Download,
  BookOpen,
  FileText,
  Tablet,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { useStore } from '../store';
import { imageApi } from '../services/imageApi';
import type { PublishConfig, PublishMetadata } from '../types/images';

interface PublishPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const FORMATS = [
  {
    id: 'epub' as const,
    name: 'ePub',
    icon: BookOpen,
    description: 'Universal eBook format for most readers',
    extension: '.epub',
  },
  {
    id: 'kindle' as const,
    name: 'Kindle',
    icon: Tablet,
    description: 'Optimized for Amazon Kindle devices',
    extension: '.epub',
  },
  {
    id: 'pdf' as const,
    name: 'PDF',
    icon: FileText,
    description: 'Print-ready document format',
    extension: '.pdf',
  },
];

export function PublishPanel({ isOpen, onClose }: PublishPanelProps) {
  const {
    sections,
    imageBlocks,
    coverDesign,
    currentSessionName,
    user,
    genericSections,
    addNotification,
  } = useStore();

  const [selectedFormat, setSelectedFormat] = useState<'epub' | 'kindle' | 'pdf'>('epub');
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<string | null>(null);

  // Get author name from generic sections or user
  const getAuthorName = () => {
    for (const section of genericSections) {
      const nameField = section.fields.find(
        (f) => f.label.toLowerCase().includes('author') || f.label.toLowerCase().includes('name')
      );
      if (nameField?.value) return nameField.value;
    }
    return user?.name || 'Unknown Author';
  };

  const [metadata, setMetadata] = useState<PublishMetadata>({
    title: currentSessionName || 'Untitled Document',
    author: getAuthorName(),
    description: '',
    isbn: '',
    language: 'en',
    publisher: '',
    publishDate: new Date().toISOString().split('T')[0],
  });

  const [options, setOptions] = useState({
    includeImages: true,
    includeCover: true,
    imageQuality: 'web' as 'web' | 'print',
  });

  const handleExport = async () => {
    if (sections.length === 0) {
      addNotification('No content to export', 'error');
      return;
    }

    setIsExporting(true);
    setExportProgress('Preparing document...');

    try {
      // Build sections with images
      const exportSections = sections.map((section) => {
        const sectionImages = imageBlocks[section.id] || [];
        return {
          title: section.title,
          content: section.content,
          images: options.includeImages
            ? sectionImages.map((img) => ({
                url: img.imageUrl,
                caption: img.caption,
                position: img.position,
              }))
            : undefined,
        };
      });

      // Build cover if available
      const cover =
        options.includeCover && coverDesign
          ? {
              imageUrl: coverDesign.imageUrl,
              title: metadata.title,
              author: metadata.author,
            }
          : undefined;

      setExportProgress('Generating document...');

      const config: PublishConfig = {
        format: selectedFormat,
        includeImages: options.includeImages,
        includeCover: options.includeCover,
        imageQuality: options.imageQuality,
        metadata,
      };

      const blob = await imageApi.exportDocument(config, exportSections, cover);

      // Download the file
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const format = FORMATS.find((f) => f.id === selectedFormat);
      link.href = url;
      link.download = `${metadata.title}${format?.extension || '.epub'}`;
      link.click();
      URL.revokeObjectURL(url);

      setExportProgress(null);
      addNotification(`Document exported as ${format?.name}`, 'success');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      addNotification(message, 'error');
    } finally {
      setIsExporting(false);
      setExportProgress(null);
    }
  };

  const totalImages = Object.values(imageBlocks).flat().length;
  const totalWords = sections.reduce(
    (sum, s) => sum + (s.content?.split(/\s+/).length || 0),
    0
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent/20 rounded-lg">
              <Download className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Publish Document</h2>
              <p className="text-sm text-gray-500">
                Export your document with images and formatting
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Format Selection */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Export Format</h3>
            <div className="grid grid-cols-3 gap-3">
              {FORMATS.map((format) => {
                const Icon = format.icon;
                return (
                  <button
                    key={format.id}
                    onClick={() => setSelectedFormat(format.id)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      selectedFormat === format.id
                        ? 'border-accent bg-accent/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon
                      className={`w-8 h-8 mb-2 ${
                        selectedFormat === format.id ? 'text-accent' : 'text-gray-400'
                      }`}
                    />
                    <h4 className="font-medium">{format.name}</h4>
                    <p className="text-xs text-gray-500 mt-1">{format.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Document Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Document Summary</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-800">{sections.length}</p>
                <p className="text-xs text-gray-500">Sections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{totalWords.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Words</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-800">{totalImages}</p>
                <p className="text-xs text-gray-500">Images</p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Metadata</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Title *</label>
                <input
                  type="text"
                  value={metadata.title}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, title: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Author *</label>
                <input
                  type="text"
                  value={metadata.author}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, author: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Publisher</label>
                <input
                  type="text"
                  value={metadata.publisher || ''}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, publisher: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">ISBN</label>
                <input
                  type="text"
                  value={metadata.isbn || ''}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, isbn: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Optional"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-600 mb-1">Description</label>
                <textarea
                  value={metadata.description || ''}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  rows={2}
                  placeholder="Optional book description"
                />
              </div>
            </div>
          </div>

          {/* Options */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">Options</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={options.includeCover}
                  onChange={(e) =>
                    setOptions((prev) => ({ ...prev, includeCover: e.target.checked }))
                  }
                  className="w-4 h-4 rounded border-gray-300 text-accent focus:ring-accent"
                />
                <span className="text-sm">Include cover page</span>
                {!coverDesign && (
                  <span className="text-xs text-amber-600">(No cover designed)</span>
                )}
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={options.includeImages}
                  onChange={(e) =>
                    setOptions((prev) => ({ ...prev, includeImages: e.target.checked }))
                  }
                  className="w-4 h-4 rounded border-gray-300 text-accent focus:ring-accent"
                />
                <span className="text-sm">Include section images ({totalImages})</span>
              </label>
              {options.includeImages && (
                <div className="ml-7">
                  <label className="block text-sm text-gray-600 mb-1">Image Quality</label>
                  <select
                    value={options.imageQuality}
                    onChange={(e) =>
                      setOptions((prev) => ({
                        ...prev,
                        imageQuality: e.target.value as 'web' | 'print',
                      }))
                    }
                    className="w-48 px-3 py-1.5 border rounded-lg text-sm"
                  >
                    <option value="web">Web (smaller file size)</option>
                    <option value="print">Print (higher quality)</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Warnings */}
          {sections.some((s) => !s.content?.trim()) && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Some sections are empty</p>
                <p className="text-sm text-amber-600">
                  Sections without content will be included but appear blank in the export.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
          {exportProgress ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              {exportProgress}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Ready to export {sections.length} section{sections.length !== 1 ? 's' : ''}
            </p>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || !metadata.title || !metadata.author}
              className="flex items-center gap-2 px-6 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export {FORMATS.find((f) => f.id === selectedFormat)?.name}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PublishPanel;
